const STORAGE_ACCESS  = 'it_access_token';
const STORAGE_REFRESH = 'it_refresh_token';

export const getAccessToken  = (): string | null => localStorage.getItem(STORAGE_ACCESS);
export const getRefreshToken = (): string | null => localStorage.getItem(STORAGE_REFRESH);

export function setTokens(access: string, refresh: string) {
  localStorage.setItem(STORAGE_ACCESS, access);
  localStorage.setItem(STORAGE_REFRESH, refresh);
}

export function clearTokens() {
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
}

// Deduplicate concurrent refresh attempts
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const rt = getRefreshToken();
    if (!rt) return false;
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) { clearTokens(); return false; }
      const { accessToken, refreshToken } = await res.json();
      setTokens(accessToken, refreshToken);
      return true;
    } catch {
      clearTokens();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function request<T>(
  path: string,
  options: RequestInit & { _isRetry?: boolean; multipart?: boolean } = {}
): Promise<T> {
  const { _isRetry, multipart, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string>),
  };

  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!multipart) headers['Content-Type'] = 'application/json';

  const res = await fetch(path, { ...fetchOptions, headers });

  if (res.status === 401 && !_isRetry) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(path, { ...options, _isRetry: true });
    }
    clearTokens();
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login?expired=1';
    }
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Request failed: ${res.status}`) as Error & { code?: string; status?: number };
    err.code   = body.code;
    err.status = res.status;
    throw err;
  }

  const text = await res.text();
  return text ? (JSON.parse(text) as T) : ({} as T);
}

export const api = {
  get:    <T>(path: string)                     => request<T>(path, { method: 'GET' }),
  post:   <T>(path: string, body?: unknown)     => request<T>(path, { method: 'POST',   body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch:  <T>(path: string, body?: unknown)     => request<T>(path, { method: 'PATCH',  body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string)                     => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData)     => request<T>(path, { method: 'POST', body: form, multipart: true }),
};
